import type { Abi, Address } from "viem"
import type { Config } from "wagmi"
import { readContract, simulateContract } from "@wagmi/core"
import { encodeFunctionData } from "viem"

import {
  thickV2NfpMulticallAbi,
  thickV2NfpPositionBurnAbi,
  thickV2NfpPositionCollectAbi,
  thickV2NfpPositionDecreaseLiquidityAbi,
  thickV2NfpPositionStateAbi,
} from "@/domains/abis/thick-v2-nfpm-abi"
import type { TransactionReturnType } from "@/domains/on-chain"
import { simulatedTransaction } from "@/domains/on-chain/simulated-transaction"

type BurnThickNfpPositionProps = {
  account: Address
  chainId: number
  config: Config
  tokenId: bigint
  /**
   * Liquidity currently held by the position, from the position store. Used to
   * build the `decreaseLiquidity` calldata; callers pass 0 only for positions
   * already fully decreased.
   */
  liquidity: bigint
  amount0Min: bigint
  amount1Min: bigint
  slippagePercent?: number
  deadline: number
  nfpManagerAddress: Address
}

/**
 * Multicall that closes a thick-v2 position in a single transaction:
 *
 *   1. `decreaseLiquidity` — withdraws all user liquidity (subject to
 *      slippage mins)
 *   2. `collect`           — collects underlying balances plus accrued fees
 *   3. `burn`               — destroys the NFT
 *
 * Step 1 is skipped (a no-op encode) when `liquidity === 0n` so positions
 * already decreased-to-zero can still be burned.
 *
 * Uses the existing `thickV2NfpMulticallAbi` rather than re-declaring it; same
 * contract target, same function signature.
 */
export const burnThickNfpPosition = async ({
  account,
  chainId,
  config,
  tokenId,
  liquidity,
  amount0Min,
  amount1Min,
  slippagePercent,
  deadline,
  nfpManagerAddress,
}: BurnThickNfpPositionProps): Promise<TransactionReturnType> => {
  try {
    const calls: `0x${string}`[] = []
    const liveLiquidity =
      await readLivePositionLiquidity({ config, chainId, tokenId, nfpManagerAddress }) ?? liquidity
    const [resolvedAmount0Min, resolvedAmount1Min] =
      slippagePercent === undefined
        ? [amount0Min, amount1Min]
        : await quoteLiveDecreaseMins({
            account,
            chainId,
            config,
            tokenId,
            liquidity: liveLiquidity,
            slippagePercent,
            deadline,
            nfpManagerAddress,
            fallbackAmount0Min: amount0Min,
            fallbackAmount1Min: amount1Min,
          })

    if (liveLiquidity > 0n) {
      calls.push(
        encodeFunctionData({
          abi: thickV2NfpPositionDecreaseLiquidityAbi as Abi,
          functionName: "decreaseLiquidity",
          args: [
            {
              tokenId,
              liquidity: liveLiquidity,
              amount0Min: resolvedAmount0Min,
              amount1Min: resolvedAmount1Min,
              deadline: BigInt(deadline),
            },
          ],
        }),
      )
    }

    calls.push(
      encodeFunctionData({
        abi: thickV2NfpPositionCollectAbi as Abi,
        functionName: "collect",
        args: [
          {
            tokenId,
            recipient: account,
            amount0Max: BigInt(1e36),
            amount1Max: BigInt(1e36),
          },
        ],
      }),
    )

    calls.push(
      encodeFunctionData({
        abi: thickV2NfpPositionBurnAbi as Abi,
        functionName: "burn",
        args: [tokenId],
      }),
    )

    const result = await simulatedTransaction({
      config,
      simulateArgs: {
        abi: thickV2NfpMulticallAbi as Abi,
        account,
        address: nfpManagerAddress,
        args: [calls],
        chainId,
        functionName: "multicall",
      },
    })

    return result
  } catch (error: unknown) {
    console.error("burnThickNfpPosition", error)
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
      code: error instanceof Error && "code" in error ? Number((error as { code: string }).code) : 0,
    }
  }
}

async function readLivePositionLiquidity({
  config,
  chainId,
  tokenId,
  nfpManagerAddress,
}: {
  config: Config
  chainId: number
  tokenId: bigint
  nfpManagerAddress: Address
}): Promise<bigint | undefined> {
  try {
    const result = await readContract(config, {
      abi: thickV2NfpPositionStateAbi,
      address: nfpManagerAddress,
      chainId,
      functionName: "positions",
      args: [tokenId],
    })
    return result[7]
  } catch {
    return undefined
  }
}

async function quoteLiveDecreaseMins({
  account,
  chainId,
  config,
  tokenId,
  liquidity,
  slippagePercent,
  deadline,
  nfpManagerAddress,
  fallbackAmount0Min,
  fallbackAmount1Min,
}: {
  account: Address
  chainId: number
  config: Config
  tokenId: bigint
  liquidity: bigint
  slippagePercent: number
  deadline: number
  nfpManagerAddress: Address
  fallbackAmount0Min: bigint
  fallbackAmount1Min: bigint
}): Promise<[bigint, bigint]> {
  if (liquidity <= 0n) return [0n, 0n]
  try {
    const simulatedDecrease = await simulateContract(config, {
      abi: thickV2NfpPositionDecreaseLiquidityAbi,
      account,
      chainId,
      functionName: "decreaseLiquidity",
      address: nfpManagerAddress,
      args: [
        {
          tokenId,
          liquidity,
          amount0Min: 0n,
          amount1Min: 0n,
          deadline: BigInt(deadline),
        },
      ],
    })
    const result = simulatedDecrease?.result as readonly [bigint, bigint] | undefined
    if (!result) return [fallbackAmount0Min, fallbackAmount1Min]
    return [applySlippage(result[0], slippagePercent), applySlippage(result[1], slippagePercent)]
  } catch {
    return [fallbackAmount0Min, fallbackAmount1Min]
  }
}

function applySlippage(amount: bigint, slippagePercent: number): bigint {
  if (amount === 0n) return 0n
  const bps = Math.max(0, Math.min(10_000, Math.floor((100 - slippagePercent) * 100)))
  return (amount * BigInt(bps)) / 10_000n
}
